
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Upload, Users, FileText } from 'lucide-react';
import { CsvUploadStep } from './csv-import/CsvUploadStep';
import { CsvReviewStep } from './csv-import/CsvReviewStep';
import { CsvRecord } from './csv-import/types';
import { useQueryClient } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';

interface CsvImportDialogProps {
  trigger?: React.ReactNode;
}

export const CsvImportDialog = ({ trigger }: CsvImportDialogProps) => {
  const queryClient = useQueryClient();
  const [open, setOpen] = React.useState(false);
  const [step, setStep] = React.useState<'upload' | 'review'>('upload');
  const [records, setRecords] = React.useState<CsvRecord[]>([]);

  const handleUploadComplete = (uploadedRecords: CsvRecord[]) => {
    setRecords(uploadedRecords);
    setStep('review');
  };

  const handleImportComplete = () => {
    queryClient.invalidateQueries({ queryKey: ['user-imports'] });
    
    setOpen(false);
    setStep('upload');
    setRecords([]);
  };

  const handleDialogClose = () => {
    setOpen(false);
    setStep('upload');
    setRecords([]);
  };

  const StepIndicator = () => (
    <div className="flex items-center gap-2 mb-4">
      <Badge variant={step === 'upload' ? 'default' : 'secondary'} className="flex items-center gap-1">
        <FileText className="h-3 w-3" />
        Upload
      </Badge>
      <div className="h-px bg-gray-300 flex-1" />
      <Badge variant={step === 'review' ? 'default' : 'secondary'} className="flex items-center gap-1">
        <Users className="h-3 w-3" />
        Review
      </Badge>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button>
            <Upload className="h-4 w-4 mr-2" />
            Import CSV
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
              <Upload className="h-5 w-5 text-white" />
            </div>
            <div>
              <DialogTitle className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Bulk User Import
              </DialogTitle>
              <p className="text-sm text-gray-600 mt-1">
                Import multiple users at once using a CSV file
              </p>
            </div>
          </div>
          <StepIndicator />
        </DialogHeader>
        
        <div className="mt-6">
          {step === 'upload' && (
            <CsvUploadStep onUploadComplete={handleUploadComplete} />
          )}
          
          {step === 'review' && (
            <CsvReviewStep 
              records={records} 
              onImportComplete={handleImportComplete}
              onBack={() => setStep('upload')}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
